import type { Probe, Finding, ProbeResult } from '../engine/types';
import { ok } from '../engine/runner';
import { registerProbe } from '../engine/registry';
import { sig } from '../engine/normalize';

const CORS_SOURCE = 'https://fetch.spec.whatwg.org/#http-cors-protocol';
const PNA_SOURCE =
  'https://developer.chrome.com/blog/private-network-access-preflight/';
const LNA_SOURCE = 'https://developer.chrome.com/blog/local-network-access/';

/**
 * Local-network port timing (security-research §5).
 *
 * WHAT THIS DOES: fetches http://127.0.0.1:<port>/ for a short list of ports on
 * the VISITOR'S OWN loopback interface, to distinguish a real desktop machine
 * (which typically has some local services running) from a locked-down sandbox
 * or cloud container (where loopback is usually closed).
 *
 * WHAT IT TOUCHES, PRECISELY:
 *   - targets ONLY 127.0.0.1 / ::1, never a routable address
 *   - each attempt is an ordinary HTTP **GET** (mode: 'no-cors'), not a bare
 *     TCP connect: the browser opens the TCP connection, sends a GET, and
 *     discards the opaque response. Earlier comments called it "a bare TCP
 *     connect", which was wrong.
 *   - makes a bounded number of short requests (7 ports, one pass)
 *   - nothing is transmitted, stored, or submitted anywhere. The result is
 *     displayed to the reader and never enters the fingerprint.
 *
 * WHY IT RUNS ON EVERY SCAN. Loopback is the visitor's own machine, the same
 * class of host the page already talks to, and the blast radius is bounded
 * (seven fixed ports, no payload, no routable address). Chrome 142+ shows a
 * Local Network Access permission prompt for fetches from a public site to
 * loopback; if the visitor blocks it, all seven attempts fail identically. The
 * probe therefore reports `permissionBlocked` and `outcomeAmbiguous` explicitly
 * so a blocked prompt is never read as "all ports closed" — it does not hide
 * behind a gate.
 *
 * RELIABILITY CAVEAT (this is important): Private Network Access / CORS means
 * most of these requests will fail CORS regardless of whether the port is open.
 * A failed CORS response is therefore ambiguous — it can mean "closed port" OR
 * "open port, blocked by PNA". We report the raw outcomes and the timing spread
 * and explicitly do NOT claim to classify open-vs-closed ports.
 */

const PORTS: Array<{ port: number; label: string }> = [
  { port: 22, label: 'ssh' },
  { port: 5900, label: 'vnc' },
  { port: 9222, label: 'chrome-devtools' },
  { port: 1080, label: 'socks-proxy' },
  { port: 5353, label: 'mdns' },
  { port: 3000, label: 'dev-server' },
  { port: 8080, label: 'http-alt' },
];

interface PortResult {
  port: number;
  label: string;
  outcome: 'connected' | 'refused-fast' | 'timeout' | 'blocked' | 'error';
  ms: number | null;
  error?: string;
}

export const portScanProbe: Probe = {
  id: 'portScan',
  label: 'Local loopback port timing',
  layer: 'webrtc',
  tier: 'client',
  category: 'hardware',
  description: 'Times loopback-only HTTP probes on the visitor’s own device',
  sources: [CORS_SOURCE, PNA_SOURCE, LNA_SOURCE],
  budgetMs: 5_000,
  // Chrome 142+ may prompt for Local Network Access on public->loopback
  // fetches. A blocked prompt produces seven identical failures; the probe
  // reports that as permissionBlocked / outcomeAmbiguous rather than as port
  // data, so it can run on every scan without pretending to measure ports.
  requiresGesture: false,

  async run(): Promise<ProbeResult> {
    const t0 = performance.now();
    // All ports in parallel. Loopback answers or refuses immediately, so the
    // wall-clock cost is ONE attempt (~ms), not seven sequential ones.
    const results = await Promise.all(
      PORTS.map(async ({ port, label }) => {
        const r = await timeLoopbackPort(port);
        return { port, label, ...r };
      }),
    );

    const elapsed = performance.now() - t0;
    const connected = results.filter((r) => r.outcome === 'connected');
    const blockadish = results.filter((r) => r.outcome === 'blocked');
    const timed = results.filter((r) => r.ms != null);
    const medianMs = timed.length ? sig(median(timed.map((r) => r.ms!)), 2) : null;

    // Permission detection. The LNA permission name is not universally
    // queryable; when the query itself fails we fall back to the error names.
    let permissionBlocked = blockadish.length > 0;
    try {
      const perms = (navigator as Navigator & { permissions?: Permissions }).permissions;
      if (perms && typeof perms.query === 'function') {
        const status = await perms.query({ name: 'local-network-access' as PermissionName });
        if (status.state === 'denied' && !connected.length) permissionBlocked = true;
      }
    } catch {
      // The permission name is unsupported here; the error names above stand.
    }

    // If every attempt produced the SAME failure classification and the timing
    // spread is under 2 ms, that is one uniform policy decision (a blocked
    // prompt, or a blanket PNA rule) rather than seven independent probes.
    const outcomes = new Set(results.map((r) => r.outcome));
    const errs = new Set(results.map((r) => r.error ?? ''));
    const spread = timed.length ? Math.max(...timed.map((r) => r.ms!)) - Math.min(...timed.map((r) => r.ms!)) : 0;
    const outcomeAmbiguous =
      results.length > 1 && outcomes.size === 1 && errs.size === 1 && spread < 2 && connected.length === 0;

    const findings: Finding[] = [];
    // A loopback dev-server or CDP port reachable from a page is a real, if
    // weak, automation-environment indicator: a genuine consumer desktop rarely
    // exposes an unauthenticated 9222/3000 to the page origin. Only reported
    // when the run was not permission-blocked.
    const automationPorts = connected.filter((r) => r.port === 9222 || r.port === 1080);
    if (automationPorts.length > 0 && !permissionBlocked) {
      findings.push({
        id: 'portscan.automation-port-open',
        severity: 'low',
        category: 'hardware',
        layer: 'webrtc',
        confidence: 'low',
        title: `Local automation/proxy port(s) reachable on loopback: ${automationPorts.map((p) => `${p.port} (${p.label})`).join(', ')}`,
        detail:
          'A remote-debugging port (9222) or a local SOCKS proxy (1080) accepted a loopback HTTP request. This can occur on a developer machine or a misconfigured container, so it is weak evidence on its own; it is reported as a raw observation for the reader, and never moves the score.',
        sources: [CORS_SOURCE],
        evidence: automationPorts.map((p) => `port ${p.port} connected in ${p.ms}ms`),
        scoreContribution: 0, // loopback timing never moves the deterministic score
      });
    }

    return {
      signals: {
        portsProbed: ok(results.length, 'client'),
        results: ok(results, 'client'),
        connectedCount: ok(connected.length, 'derived'),
        medianMs: ok(medianMs, 'derived'),
        totalMs: ok(sig(elapsed, 2), 'derived'),
        permissionBlocked: ok(permissionBlocked, 'derived'),
        // True when every attempt failed identically: that is one policy
        // decision (blocked prompt / blanket PNA), not a port measurement.
        outcomeAmbiguous: ok(outcomeAmbiguous, 'derived'),
        // Explicit honesty signal: we do NOT claim to know which ports were
        // open, because PNA/CORS makes the outcomes ambiguous.
        classificationAvailable: ok(false, 'derived'),
        note: ok(
          'Outcomes are ambiguous under Private Network Access / Local Network Access: a CORS failure can mean a closed port OR an open one blocked by policy, and a blocked permission prompt makes all seven attempts fail identically (see permissionBlocked / outcomeAmbiguous). Reported raw, not classified.',
          'client',
        ),
      },
      findings,
      // Deliberately NOT added to any fingerprint part: this result depends on
      // transient local state, so including it would make the fingerprint
      // depend on which local services happened to be running.
    };
  },
};

/**
 * Time a single loopback HTTP GET, and classify the OUTCOME without claiming
 * open-vs-closed (PNA/LNA makes it ambiguous).
 */
async function timeLoopbackPort(
  port: number,
): Promise<{ outcome: PortResult['outcome']; ms: number | null; error?: string }> {
  const controller = new AbortController();
  // Short per-attempt budget. Loopback should answer (or refuse) immediately;
  // this only bounds a pathologically filtered packet.
  const timer = setTimeout(() => controller.abort(), 800);
  const t0 = performance.now();
  try {
    const res = await fetch(`http://127.0.0.1:${port}/`, {
      mode: 'no-cors',
      cache: 'no-store',
      signal: controller.signal,
    });
    const ms = performance.now() - t0;
    clearTimeout(timer);
    // no-cors gives an opaque response for a successful connect.
    return { outcome: res.type === 'opaque' || res.ok ? 'connected' : 'refused-fast', ms };
  } catch (e) {
    const ms = performance.now() - t0;
    clearTimeout(timer);
    const name = (e as { name?: string })?.name;
    const message = String((e as Error)?.message ?? e);
    if (name === 'AbortError') return { outcome: 'timeout', ms, error: name };
    // Chrome surfaces a blocked Local Network Access prompt as NotAllowedError
    // (or a TypeError whose message mentions permission/local network).
    if (name === 'NotAllowedError' || /permission|local network/i.test(message)) {
      return { outcome: 'blocked', ms, error: `${name}: ${message}` };
    }
    return { outcome: 'refused-fast', ms, error: name ?? 'Error' };
  }
}

function median(xs: number[]): number {
  const s = [...xs].sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)];
}

registerProbe(portScanProbe);
